import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/userContext"
import { useLazyQuery } from "@apollo/client"
import { GET_SERVICES_EPISODES_BY_PARTICIPANT_ID } from "./queries"
import _ from "lodash"
import { Dropdown } from "primereact/dropdown"
import RequestDialog from "./components/RequestDialog"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import LoaderFullPage from "../../components/Loaders"
import CustomTable from "../../components/CustomTable/CustomTable"
import {
  formatToLocalDate,
  parseToISOString,
} from "../../helpers/dateConversion"

//Styles
import styles from "../../styles/ServiceEpisodes.module.css"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"

export default function ServiceEpisodes() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const userCtx = useContext(UserContext)
  const [organizationFilter, setOrganizationFilter] = useState(null)
  const [statusFilter, setStatusFilter] = useState(null)
  const [locationFilter, setLocationFilter] = useState(null)
  const [dialog, setDialog] = useState(false)
  const [assignedOrganization, setAssignedOrganization] = useState(null)
  const [assignedEnterprise, setaAssignedEnterprise] = useState(null)
  const [assignedStaff, setAssignedStaff] = useState(null)
  const [staffEmailAddress, setStaffEmailAddress] = useState(null)
  const [staffPhoneNumber, setStaffPhoneNumber] = useState(null)
  const [staffID, setStaffID] = useState(null)
  const [notificationType, setNotificationType] = useState(null)

  const [getData] = useLazyQuery(GET_SERVICES_EPISODES_BY_PARTICIPANT_ID, {
    variables: {
      participant_id: userCtx.participant_id,
    },
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      if (data.getServiceEpisodesParticipantPortal === null) {
        setData(null)
        return setLoading(false)
      }
      const episodes = data?.getServiceEpisodesParticipantPortal || []

      const mapOrganization = (organization) => {
        if (!organization) {
          return "Unknown Organization"
        }
        if (organization === false) {
          return "Unknown Organization"
        }
        return organization
      }

      const handleData = () =>
        _.map(episodes, (episode) => {
          let actionButton

          const isActiveEpisode = episode.end_date === null
          isActiveEpisode
            ? (actionButton = (
                <Dropdown
                  placeholder="Select action"
                  className={styles.dischargeBtn}
                  panelClassName={styles.dischargeBtnDropdown}
                  value={notificationType}
                  onChange={(e) => {
                    e.preventDefault()
                    setNotificationType(e.value)
                    setAssignedStaff(episode.episode_assigned_staff_ids)
                    setaAssignedEnterprise(episode.enterprise_id)
                    setAssignedOrganization(episode.organization_id)
                    setDialog(true)
                    setStaffEmailAddress(episode.staff_email_address)
                    setStaffPhoneNumber(episode.staff_phone_number)
                    setStaffID(episode.staff_id)
                  }}
                  optionLabel="name"
                  optionValue="code"
                  options={[
                    { name: "Request discharge", code: "discharge" },
                    { name: "Request new staff", code: "new_staff" },
                  ]}
                />
              ))
            : (actionButton = "")

          return {
            start_date: parseToISOString(episode?.start_date, "MM-dd-yyyy"),
            end_date: parseToISOString(episode?.end_date, "MM-dd-yyyy"),
            status: isActiveEpisode ? "Open" : "Closed",
            organization: mapOrganization(episode?.organization_name),
            location: episode?.location_name
              ? episode.location_name
              : "Not Set",
            staff: episode?.staff_name,
            actions: actionButton,
          }
        })
      setData(handleData())

      const allLocations = _.map(handleData(), ({ location }) => location)
      setLocationFilter(_.uniq(_.compact(allLocations)).sort())

      const allOrganizations = _.map(
        handleData(),
        ({ organization }) => organization,
      )
      setOrganizationFilter(_.uniq(allOrganizations).sort())

      const allServices = _.map(handleData(), ({ status }) => status)
      setStatusFilter(_.uniq(allServices).sort())

      setLoading(false)
    },
  })

  useEffect(() => {
    getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dialogProps = {
    dialog,
    setDialog,
    setAssignedStaff,
    setaAssignedEnterprise,
    setAssignedOrganization,
    setStaffEmailAddress,
    setStaffPhoneNumber,
    setStaffID,
    assignedStaff,
    notificationType,
    assignedOrganization,
    assignedEnterprise,
    staffEmailAddress,
    staffPhoneNumber,
    staffID,
  }

  const headers = () => {
    return [
      {
        value: "startDate",
        label: "Intake date",
        size: 2,
      },
      {
        value: "endDate",
        label: "Discharge date",
        size: 2,
      },
      {
        value: "status",
        label: "Status",
        size: 1,
      },
      {
        value: "organization",
        label: "Organization",
        size: 3,
      },
      {
        value: "location",
        label: "Location",
        size: 2,
      },
      {
        value: "action",
        label: "",
        action: true,
      },
    ]
  }

  const filters = () => {
    return [
      {
        value: "organization",
        label: "Organization",
        options: organizationFilter,
        placeholder: "Select organization",
        showClear: false,
        type: "multiselect",
      },
      {
        value: "location",
        label: "Location",
        options: locationFilter,
        placeholder: "Select location",
        showClear: true,
        type: "dropdown",
      },
      {
        value: "_status",
        label: "Status",
        options: statusFilter,
        placeholder: "Select status",
        showClear: true,
        type: "dropdown",
      },
    ]
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Service Start Date", code: "startDate" },
          { name: "Service End Date", code: "endDate" },
          { name: "Service status", code: "status" },
          { name: "Organization", code: "organization" },
          { name: "Location", code: "location" },
        ],
        default: ["startDate"],
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  const mapData = () => {
    return data?.map((d) => {
      return {
        organization: d.organization,
        location: d.location,
        startDate: formatToLocalDate(d.start_date),
        endDate: d.end_date ? formatToLocalDate(d.end_date) : "N/A",
        status: (
          <div
            className={`${styles.status} ${
              d.status === "Closed" ? styles.statusClosed : styles.statusOpen
            }`}
          >
            {d.status}
          </div>
        ),
        _status: d.status,
        action: d.actions,
      }
    })
  }

  return (
    <>
      <RequestDialog {...dialogProps} />
      <div className="main-content">
        <AppBreadcrumb items={[{ label: "Service Episodes" }]} />
        <div className="w-full pb-5">
          <StandardPageHeader
            title="Service Episodes"
            refreshData={() => {
              setLoading(true)
              getData()
            }}
          />

          <hr className="line" />

          {loading ? (
            <LoaderFullPage />
          ) : (
            <CustomTable
              headers={headers()}
              sorting={sorting()}
              filters={filters()}
              collection={mapData()}
            />
          )}
        </div>
      </div>
    </>
  )
}
